import { api } from '../api';
import type {
  DocumentApprovalActionRow,
  DocumentClassificationRow,
  DocumentRow,
  DocumentTypeRow,
  OffsetPaginated,
} from '@/types/domain';

export interface DocumentListParams {
  entityId?: number;
  documentTypeId?: number;
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export type DocumentListResponse = OffsetPaginated<DocumentRow> & {
  /** Mantengo el campo por compat con código legacy; siempre `null` ahora. */
  nextCursor?: number | null;
};

function buildDocQuery(params: DocumentListParams): string {
  const search = new URLSearchParams();
  if (params.entityId) search.set('entityId', String(params.entityId));
  if (params.documentTypeId) search.set('documentTypeId', String(params.documentTypeId));
  if (params.status) search.set('status', params.status);
  if (params.q) search.set('q', params.q.slice(0, 256));
  if (params.limit) search.set('limit', String(params.limit));
  if (params.offset) search.set('offset', String(params.offset));
  const qs = search.toString();
  return qs ? `/documents?${qs}` : '/documents';
}

export interface DocumentTypePromptRow {
  id: number;
  version: number;
  promptText: string;
  active: boolean;
  createdAt: string;
  notes: string | null;
}

type DocumentTypeApiRow = DocumentTypeRow & {
  applicableEntityTypes?: Array<{ entityTypeId: number }>;
};

function normalizeDocumentType(response: DocumentTypeApiRow): DocumentTypeRow {
  return {
    ...response,
    extractionSchema: normalizeExtractionSchema(response.extractionSchema),
    applicableEntityTypeIds: response.applicableEntityTypeIds?.length
      ? response.applicableEntityTypeIds
      : response.applicableEntityTypes?.map((row) => row.entityTypeId) ?? [],
  };
}

function normalizeExtractionSchema(raw: unknown): DocumentTypeRow['extractionSchema'] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object' && raw !== null && Array.isArray((raw as { fields?: unknown }).fields)) {
    return (raw as { fields: DocumentTypeRow['extractionSchema'] }).fields;
  }
  return [];
}

export const documentTypesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listDocumentTypes: builder.query<DocumentTypeRow[], void>({
      query: () => '/document-types',
      transformResponse: (response: DocumentTypeApiRow[]) =>
        response.map((row) => normalizeDocumentType(row)),
      providesTags: [{ type: 'DocumentType', id: 'LIST' }],
    }),
    getDocumentType: builder.query<DocumentTypeRow, number>({
      query: (id) => `/document-types/${id}`,
      transformResponse: (response: DocumentTypeApiRow) => normalizeDocumentType(response),
      providesTags: (_r, _e, id) => [{ type: 'DocumentType', id }],
    }),
    getDocumentTypePrompts: builder.query<DocumentTypePromptRow[], number>({
      query: (id) => `/document-types/${id}/prompts`,
      providesTags: (_r, _e, id) => [{ type: 'DocumentType', id: `prompts-${id}` }],
    }),
    createDocumentTypePrompt: builder.mutation<
      DocumentTypePromptRow,
      { id: number; promptText: string; notes?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/document-types/${id}/prompts`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'DocumentType', id: `prompts-${id}` }],
    }),
    createDocumentType: builder.mutation<DocumentTypeRow, Record<string, unknown>>({
      query: (body) => ({ url: '/document-types', method: 'POST', body }),
      invalidatesTags: [{ type: 'DocumentType', id: 'LIST' }],
    }),
    updateDocumentType: builder.mutation<DocumentTypeRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/document-types/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'DocumentType', id }, { type: 'DocumentType', id: 'LIST' }],
    }),
    deleteDocumentType: builder.mutation<void, number>({
      query: (id) => ({ url: `/document-types/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'DocumentType', id: 'LIST' }],
    }),
  }),
});

export const documentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listDocuments: builder.query<DocumentListResponse, DocumentListParams>({
      query: (params) => buildDocQuery(params),
      providesTags: (result) => [
        ...(result?.items ?? []).map(({ id }) => ({ type: 'Document' as const, id })),
        { type: 'Document', id: 'LIST' },
      ],
    }),
    getDocument: builder.query<DocumentRow, number>({
      query: (id) => `/documents/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Document', id }],
    }),
    getDocumentHistory: builder.query<DocumentRow[], number>({
      query: (id) => `/documents/${id}/history`,
    }),
    getDocumentClassification: builder.query<DocumentClassificationRow, number>({
      query: (id) => `/documents/${id}/classification`,
    }),
    getDocumentApprovalActions: builder.query<DocumentApprovalActionRow[], number>({
      query: (id) => `/documents/${id}/approval-actions`,
    }),
    forwardDocument: builder.mutation<
      { forwarded: boolean },
      { id: number; forwardToUserId?: number; comment?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/documents/${id}/forward`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Document', id },
        { type: 'Approval', id: 'QUEUE' },
      ],
    }),
    getUploadUrl: builder.mutation<
      { uploadUrl: string; objectKey: string; expiresIn: number },
      { mimeType: string; fileSize: number; fileName: string }
    >({
      query: (body) => ({ url: '/documents/upload-url', method: 'POST', body }),
    }),
    createDocument: builder.mutation<DocumentRow, Record<string, unknown>>({
      query: (body) => ({ url: '/documents', method: 'POST', body }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),
    getDownloadLink: builder.query<{ url: string }, number>({
      query: (id) => `/documents/${id}/download-link`,
    }),
    approveDocument: builder.mutation<DocumentRow, { id: number; comment?: string }>({
      query: ({ id, comment }) => ({
        url: `/documents/${id}/approve`,
        method: 'POST',
        body: comment ? { comment } : {},
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Document', id },
        { type: 'Document', id: 'LIST' },
        { type: 'Approval', id: 'QUEUE' },
      ],
    }),
    rejectDocument: builder.mutation<
      DocumentRow,
      { id: number; reason: string; category: string }
    >({
      query: ({ id, reason, category }) => ({
        url: `/documents/${id}/reject`,
        method: 'POST',
        body: { reason, category },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Document', id },
        { type: 'Document', id: 'LIST' },
        { type: 'Approval', id: 'QUEUE' },
      ],
    }),
    bulkApproveDocuments: builder.mutation<{ count: number }, { documentIds: number[]; comment?: string }>({
      query: (body) => ({ url: '/documents/bulk-approve', method: 'POST', body }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }, { type: 'Approval', id: 'QUEUE' }],
    }),
    bulkRejectDocuments: builder.mutation<
      { count: number },
      { documentIds: number[]; reason?: string; category?: string }
    >({
      query: (body) => ({ url: '/documents/bulk-reject', method: 'POST', body }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }, { type: 'Approval', id: 'QUEUE' }],
    }),
    renewDocument: builder.mutation<DocumentRow, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/documents/${id}/renew`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Document', id },
        { type: 'Document', id: 'LIST' },
      ],
    }),
    getRejectedDashboard: builder.query<
      {
        total: number;
        byEntityType: Record<string, number>;
        topReasons: Array<{ reason: string; count: number }>;
        items: Array<{
          id: number;
          documentTypeName: string;
          entityTypeName: string;
          entityDisplayName: string;
          entityIdentifier: string;
          reason: string;
          rejectedAt: string;
        }>;
      },
      { entityTypeId?: number; limit?: number; offset?: number } | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.entityTypeId) search.set('entityTypeId', String(params.entityTypeId));
        if (params?.limit) search.set('limit', String(params.limit));
        if (params?.offset) search.set('offset', String(params.offset));
        const qs = search.toString();
        return qs ? `/documents/rejected-dashboard?${qs}` : '/documents/rejected-dashboard';
      },
      providesTags: [{ type: 'Document', id: 'REJECTED_DASHBOARD' }],
    }),
  }),
});

export const {
  useListDocumentTypesQuery,
  useGetDocumentTypeQuery,
  useGetDocumentTypePromptsQuery,
  useCreateDocumentTypePromptMutation,
  useCreateDocumentTypeMutation,
  useUpdateDocumentTypeMutation,
  useDeleteDocumentTypeMutation,
} = documentTypesApi;

export const {
  useListDocumentsQuery,
  useGetDocumentQuery,
  useGetDocumentHistoryQuery,
  useGetDocumentClassificationQuery,
  useGetDocumentApprovalActionsQuery,
  useForwardDocumentMutation,
  useGetUploadUrlMutation,
  useCreateDocumentMutation,
  useGetDownloadLinkQuery,
  useLazyGetDownloadLinkQuery,
  useApproveDocumentMutation,
  useRejectDocumentMutation,
  useBulkApproveDocumentsMutation,
  useBulkRejectDocumentsMutation,
  useRenewDocumentMutation,
  useGetRejectedDashboardQuery,
} = documentsApi;
